import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { useRecoilState } from "recoil";
import { SPANISH_ABBREV } from "@bto/shared";
import {
  CreateAccountDesktopContainer,
  CreateAccountDesktopColorBackground,
  CreateAccountDesktopGradientLayer,
  CreateAccountDesktopBrandPanel,
  CreateAccountDesktopBrandLockup,
  CreateAccountDesktopFormCell,
  CreateAccountNonDesktopShell,
  CreateAccountHeroBand,
  CreateAccountHeroLockupImg,
  CreateAccountNonDesktopContents,
} from "../CreateAccount/CreateAccountForm.styles";
import unityLockup from "../../assets/logos/unity-rebrand/unity-vert-cobranded.svg";
import unityLockupHorizontal from "../../assets/logos/unity-rebrand/unity-horiz-cobranded.svg";
import { VerifyEmailConfirmedSection } from "./VerifyEmailConfirmedSection";
import VerifyEmailRequest from "../../services/VerifyEmailService";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { RefreshPatientInfo } from "../CreateAccount/Services/CreateAccountService";
import { patientState } from "../../recoil/atoms";
import { LOGIN_PAGE_URL } from "../../utils/urlConstants";
import { ErrorPopup } from "../shared/ErrorPopup/ErrorPopup";
import { getCookieAccessToken } from "../../utils/requestUtils";
import { getLanguageFromQueryParam } from "../../i18n/languagePreference";

export const VerifyEmailConfirmed = (): React.ReactElement => {
  const { t } = useTranslation();
  const { isDesktop } = useDeviceHook();
  const history = useHistory();
  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [canSetSpanishPreferred, setCanSetSpanishPreferred] = useState(false);
  const { verifyJwt } = useParams<{ verifyJwt: string }>();
  const [, setPatientInfo] = useRecoilState(patientState);

  const sendVerifyEmailCode = async () => {
    const response = await VerifyEmailRequest(verifyJwt);
    if (response && response?.ok) {
      const authToken = getCookieAccessToken();
      if (authToken) {
        await RefreshPatientInfo(authToken, setPatientInfo);
        // If the patient arrived via the Spanish verification link, surface an
        // explicit link to set Spanish as their saved preference (the patient
        // opts in — we do not auto-persist). Only when authenticated.
        if (
          getLanguageFromQueryParam(window.location.search) === SPANISH_ABBREV
        ) {
          setCanSetSpanishPreferred(true);
        }
      }
      setEmail(await response.json());
    } else {
      setErrorMessage(t("verifyEmail.errors.verificationFailed"));
    }
  };

  useEffect(() => {
    if (!verifyJwt) {
      history.push(LOGIN_PAGE_URL);
    }
    sendVerifyEmailCode();
  }, []);

  if (!verifyJwt) {
    return <></>;
  }

  return (
    <>
      <ErrorPopup message={errorMessage} hideMessage={setErrorMessage} />
      {isDesktop && (
        <CreateAccountDesktopContainer>
          <CreateAccountDesktopColorBackground>
            <CreateAccountDesktopGradientLayer aria-hidden />
            <CreateAccountDesktopBrandPanel>
              <CreateAccountDesktopBrandLockup
                src={unityLockup}
                alt="Unity by BillionToOne"
              />
            </CreateAccountDesktopBrandPanel>
          </CreateAccountDesktopColorBackground>
          <CreateAccountDesktopFormCell>
            <VerifyEmailConfirmedSection
              email={email}
              canSetSpanishPreferred={canSetSpanishPreferred}
            />
          </CreateAccountDesktopFormCell>
        </CreateAccountDesktopContainer>
      )}
      {!isDesktop && (
        <CreateAccountNonDesktopShell>
          <CreateAccountHeroBand>
            <CreateAccountHeroLockupImg
              src={unityLockupHorizontal}
              alt="Unity by BillionToOne"
            />
          </CreateAccountHeroBand>
          <CreateAccountNonDesktopContents>
            <VerifyEmailConfirmedSection
              email={email}
              canSetSpanishPreferred={canSetSpanishPreferred}
            />
          </CreateAccountNonDesktopContents>
        </CreateAccountNonDesktopShell>
      )}
    </>
  );
};
